import { useCallback, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  FluentProvider, webLightTheme, webDarkTheme,
  Button, Text, makeStyles, tokens, shorthands,
} from '@fluentui/react-components';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Shell } from './components/Shell';
import { Overview } from './pages/Overview';
import { PillarPage } from './pages/PillarPage';
import { Frameworks } from './pages/Frameworks';
import { FrameworkDetail } from './pages/FrameworkDetail';
import { Tenants } from './pages/Tenants';
import { TenantDetail } from './pages/TenantDetail';
import { RunFindings } from './pages/RunFindings';
import { Catalogue } from './pages/Catalogue';
import { CustomerProvider } from './context/CustomerContext';
import { ApiProvider, useApi } from './api/context';
import { useAsync } from './api/useAsync';
import { authConfigured, loginRequest } from './auth/msal';

const useStyles = makeStyles({
  signIn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh', rowGap: tokens.spacingVerticalL,
    ...shorthands.padding(tokens.spacingVerticalXXL),
    textAlign: 'center',
  },
  note: { color: tokens.colorNeutralForeground3, maxWidth: '460px' },
});

function SignIn() {
  const styles = useStyles();
  const { instance } = useMsal();

  return (
    <div className={styles.signIn}>
      <Text size={700} weight="semibold">Cloud Security Assessment</Text>
      <Text className={styles.note}>
        Sign in with your SoftwareOne account. There is no separate password for
        this tool, so multi factor authentication and Conditional Access apply as
        they do everywhere else.
      </Text>
      {/* Redirect, not popup.
          A popup gets its own sessionStorage, so MSAL cannot hand the result
          back to the parent window: authentication succeeds, the popup reloads
          this same app inside itself, and the original window never changes.
          Redirect has no cross window handoff to fail. */}
      <Button appearance="primary" size="large" onClick={() => void instance.loginRedirect(loginRequest)}>
        Sign in
      </Button>
    </div>
  );
}

function Configure() {
  const styles = useStyles();
  return (
    <div className={styles.signIn}>
      <Text size={700} weight="semibold">Authentication is not configured</Text>
      <Text className={styles.note}>
        Set VITE_ENTRA_TENANT_ID, VITE_ENTRA_CLIENT_ID and VITE_API_SCOPE in a
        .env.local file, then restart the dev server. See docs/SETUP.md.
      </Text>
    </div>
  );
}

function AuthenticatedApp() {
  const api = useApi();
  const stamp = useAsync(() => api.getStamp(), []);
  const [isDark, setIsDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );
  const toggle = useCallback(() => setIsDark((d) => !d), []);

  return (
    <FluentProvider theme={isDark ? webDarkTheme : webLightTheme}>
      <CustomerProvider>
      <Shell
        stampRegion={stamp.data?.region}
        onToggleTheme={toggle}
        isDark={isDark}
      >
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/pillars/:pillarId" element={<PillarPage />} />
          <Route path="/frameworks" element={<Frameworks />} />
          <Route path="/frameworks/:frameworkId" element={<FrameworkDetail />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/tenants" element={<Tenants />} />
          <Route path="/tenants/:tenantId" element={<TenantDetail />} />
          <Route path="/runs/:runId" element={<RunFindings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
      </CustomerProvider>
    </FluentProvider>
  );
}

export function App() {
  const isAuthenticated = useIsAuthenticated();

  if (!authConfigured) {
    return (
      <FluentProvider theme={webLightTheme}>
        <Configure />
      </FluentProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <FluentProvider theme={webLightTheme}>
        <SignIn />
      </FluentProvider>
    );
  }

  return (
    <ApiProvider>
      <AuthenticatedApp />
    </ApiProvider>
  );
}
